/**
 * /admin/vat — UK VAT threshold monitor (admin/owner only).
 *
 * Shows trailing-12-month revenue vs the £90,000 threshold, with alert
 * status, recipient list, and a "Send test alert" button so the routing
 * can be verified without waiting for revenue to actually climb.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, RefreshCw, Send, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrAbove } from '../lib/tier';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) =>
  `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB'); } catch (e) { return iso; }
};

const AdminVat = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vat/state`, { withCredentials: true });
      setState(r.data);
    } catch (err) {
      console.error('VAT state load failed', err);
      toast.error('Could not load VAT state.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminOrAbove(user)) return <Navigate to="/home" replace />;

  const runCheck = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/vat/check`, {}, { withCredentials: true });
      if (r.data.level) {
        toast.success(`Alert fired: ${r.data.level} (£${r.data.revenue.toFixed(2)}).`);
      } else {
        toast.success('Check complete — no alert needed.');
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check failed.');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/vat/test-alert`, {}, { withCredentials: true });
      toast.success(`Test alert sent to ${r.data.recipients.length} recipient${r.data.recipients.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed.');
    } finally {
      setBusy(false);
    }
  };

  const resetState = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/vat/reset`, {}, { withCredentials: true });
      toast.success('Re-armed. Next climb above £85k / £88k will alert.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !state) {
    return <div className="p-8 text-center text-[#64748B]">Loading VAT state…</div>;
  }

  const pct = state.percent_used;
  const warningPct = (state.warning_at / state.threshold) * 100;
  const triggerPct = (state.trigger_at / state.threshold) * 100;

  const bandColor =
    state.current_revenue >= state.trigger_at ? '#DC2626'
    : state.current_revenue >= state.warning_at ? '#B45309'
    : '#0052FF';

  return (
    <div className="max-w-4xl mx-auto p-6 sm:p-8" data-testid="admin-vat-page">
      <Link to="/admin/tiers" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <PageTitle
        title="UK VAT trigger monitor"
        subtitle="Trailing 12-month Stripe revenue vs the £90,000 registration threshold."
      />

      {/* Revenue + headroom card */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-[#64748B]">Trailing 12-month revenue</div>
            <div className="text-4xl font-extrabold tracking-tight" style={{ color: bandColor }} data-testid="vat-revenue">
              {fmtMoney(state.current_revenue)}
            </div>
            <div className="text-sm text-[#64748B] mt-1">
              Headroom to £90k: <strong>{fmtMoney(state.headroom)}</strong>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider font-bold text-[#64748B]">As of</div>
            <div className="text-sm text-[#020617]">{fmtDate(state.as_of)}</div>
            {state.last_checked_at && (
              <div className="text-xs text-[#94A3B8] mt-1">Last automated check: {fmtDate(state.last_checked_at)}</div>
            )}
          </div>
        </div>

        {/* Headroom bar */}
        <div className="relative h-4 rounded-full bg-[#F1F5F9] overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.min(100, pct)}%`, background: bandColor }}
            data-testid="vat-bar-fill"
          />
          {/* Warning marker */}
          <div
            className="absolute top-0 bottom-0 w-px bg-[#B45309]"
            style={{ left: `${warningPct}%` }}
            title={`Warning at ${fmtMoney(state.warning_at)}`}
          />
          {/* Trigger marker */}
          <div
            className="absolute top-0 bottom-0 w-px bg-[#DC2626]"
            style={{ left: `${triggerPct}%` }}
            title={`Trigger at ${fmtMoney(state.trigger_at)}`}
          />
        </div>
        <div className="flex justify-between text-[10px] uppercase tracking-wider font-bold text-[#64748B] mt-2">
          <span>£0</span>
          <span style={{ marginLeft: `${warningPct - 2}%` }} className="absolute">£85k</span>
          <span style={{ marginLeft: `${triggerPct - 2}%` }} className="absolute">£88k</span>
          <span>£90k</span>
        </div>
      </div>

      {/* Alert status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-[#FCD34D]/50 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-[#B45309] mb-1">
            ⚠️ Warning ({fmtMoney(state.warning_at)})
          </div>
          <div className="text-sm text-[#020617]">
            {state.last_warning_at ? (
              <>Fired: <strong>{fmtDate(state.last_warning_at)}</strong></>
            ) : (
              <span className="text-[#64748B]">Not fired yet — armed.</span>
            )}
          </div>
        </div>
        <div className="bg-white border border-[#FCA5A5]/50 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-[#DC2626] mb-1">
            ⛔ Trigger ({fmtMoney(state.trigger_at)})
          </div>
          <div className="text-sm text-[#020617]">
            {state.last_trigger_at ? (
              <>Fired: <strong>{fmtDate(state.last_trigger_at)}</strong></>
            ) : (
              <span className="text-[#64748B]">Not fired yet — armed.</span>
            )}
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="vat-recipients-card">
        <div className="text-xs uppercase tracking-wider font-bold text-[#475569] mb-2">
          Alert recipients ({state.recipients.length})
        </div>
        {state.recipients.length === 0 ? (
          <p className="text-sm text-[#64748B]">No recipients configured — alerts would have nowhere to go.</p>
        ) : (
          <ul className="text-sm text-[#020617] space-y-1">
            {state.recipients.map((email) => (
              <li key={email} className="flex items-center justify-between" data-testid={`vat-recipient-${email}`}>
                <code className="bg-[#F8FAFC] px-2 py-0.5 rounded text-xs">{email}</code>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-[#64748B] mt-3 leading-relaxed">
          Recipients = every user with role <code>owner</code>, <code>admin</code> or <code>staff</code>,
          plus every entry in the <code>VAT_ALERT_EMAILS</code> env var, dedup&apos;d.
          Grant/revoke a role on <Link to="/admin/tiers" className="text-[#0052FF] hover:underline">/admin/tiers</Link>.
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={runCheck} disabled={busy} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="vat-run-check">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> Run check now
        </Button>
        <Button onClick={sendTest} disabled={busy} variant="outline" data-testid="vat-send-test">
          <Send className="w-4 h-4 mr-2" /> Send test alert
        </Button>
        <Button onClick={resetState} disabled={busy} variant="outline" data-testid="vat-reset">
          <RotateCcw className="w-4 h-4 mr-2" /> Re-arm alerts
        </Button>
      </div>

      <p className="text-xs text-[#94A3B8] mt-6 leading-relaxed">
        Revenue is the sum of paid <code>payment_transactions</code> in GBP over the last 365 days.
        Refunds/disputes are not subtracted (conservative estimate).
        The automated check runs daily at 09:00 UTC via the scheduler.
      </p>
    </div>
  );
};

export default AdminVat;
